import { useState } from 'react';

import { useTranslation } from 'i18n/hooks';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Drawer,
  SpacedContainer,
} from 'revibe-ui';
import { Accordion } from 'revibe-ui';

import { SizesGuidance } from 'modules/catalog/components/SizesGuidance';
import { MarketplaceAPI } from 'revibe-api';
import { fontVariables } from 'ui/fonts/fontVariables';

interface Props {
  item: MarketplaceAPI.Item;
}

export const ItemAdditionalInfoAccordion = ({ item }: Props) => {
  const { tu } = useTranslation('shop');
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  const entries = [
    {
      header: tu('upcycling-sustainability', 'common').toUpperCase(),
      content: (
        <div className="text-sm text-gray-700">
          <p className="whitespace-pre-wrap">
            {tu('upcycling-disclaimer1')}
            <br />
            <br />
            {tu('upcycling-disclaimer2')}
          </p>
        </div>
      ),
    },
    {
      header: tu('sizes-info').toUpperCase(),
      content: (
        <div className="text-sm text-gray-700">
          <p className="whitespace-pre-wrap">
            {item.sizes_info || tu('no-sizes-info')}
          </p>
          <p
            className="mt-8 cursor-pointer underline"
            onClick={() => setIsGuideOpen(true)}
          >
            {tu('size-guide', 'shop')}
          </p>
          <Drawer
            scrollable
            position="bottom"
            size="xl"
            open={isGuideOpen}
            onOpenChange={() => setIsGuideOpen(false)}
            className={fontVariables}
          >
            <SizesGuidance alternative={item.alternative_size_guide} />
          </Drawer>
        </div>
      ),
    },
  ];

  if (item.traceability) {
    entries.push({
      header: tu('traceability').toUpperCase(),
      content: (
        <div className="text-sm text-gray-700">
          <p className="whitespace-pre-wrap">{item.traceability}.</p>
        </div>
      ),
    });
  }

  if (item.available_for_preorder) {
    entries.push({
      header: tu('orders.how-preorder-works-heading').toUpperCase(),
      content: (
        <div className="text-sm text-gray-700">
          <p className="whitespace-pre-wrap">
            {tu('made-to-order-explanation1', 'shop.orders')}.
            <br />
            <br />
            {tu('made-to-order-explanation2', 'shop.orders')}.
          </p>
        </div>
      ),
    });
  }

  entries.push({
    header: tu('about-the-item').toUpperCase(),
    content: (
      <div className="text-sm text-gray-700">
        <SpacedContainer>
          <div className="whitespace-pre-wrap">
            <h2 className="font-semibold">{tu('description')}</h2>
            <p className="whitespace-pre-wrap">{item.description}</p>
          </div>
          {item.materials && (
            <div className="whitespace-pre-wrap">
              <h2 className="font-semibold">
                {tu('materials-and-composition')}
              </h2>
              <p className="whitespace-pre-wrap">{item.materials}</p>
            </div>
          )}
          {item.maintenance_tips && (
            <div className="whitespace-pre-wrap">
              <h2 className="font-semibold">{tu('maintenance-tips')}</h2>
              <p className="whitespace-pre-wrap">{item.maintenance_tips}</p>
            </div>
          )}
        </SpacedContainer>
      </div>
    ),
  });

  return (
    <Accordion type="single" collapsible>
      {entries.map(({ header, content }) => (
        <AccordionItem key={header} value={header}>
          <AccordionTrigger className="p-4">{header}</AccordionTrigger>
          <AccordionContent className="p-4">{content}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};
